import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  Paper,
  Box,
  Grid,
  Chip,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { handleTableData } from "./redux/syllabusSlice";
import { fetchSessionsThunk } from "../AcademicCalendar/redux/academicCalendarSlice";
import { submitSyllabusThunk } from "./redux/syllabusthunk";
import { useLocation, useNavigate } from "react-router-dom";

const SyllabusStage2 = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { tableData } = useSelector((state) => state.syllabus);
  const { teacherPeriods } = useSelector((state) => state.myTimetable);
  const sessions = useSelector((state) => state.academicCalendar.sessions);
  const formData = location.state;

  const [termStats, setTermStats] = useState({});

  useEffect(() => {
    if (!formData) {
      alert("Missing form data. Please upload again.");
      navigate("/uploadSyllabus");
    }
  }, [formData, navigate]);

  useEffect(() => {
    dispatch(fetchSessionsThunk());
  }, [dispatch]);

  const countTermActualPeriods = (periods, termStart, termEnd) => {
    const flatList = [];
    const cursor = termStart.clone();
    while (cursor.isSameOrBefore(termEnd, "day")) {
      const dayName = cursor.format("dddd");
      const dayPeriods = periods.filter((p) => p.day === dayName);
      dayPeriods.forEach((p) =>
        flatList.push({ ...p, date: cursor.format("YYYY-MM-DD") })
      );
      cursor.add(1, "day");
    }
    return flatList.length;
  };

  useEffect(() => {
    if (!sessions?.length || !teacherPeriods?.periods) return;

    const session = sessions[0];
    const start = moment(session.start_date);
    const end = moment(session.end_date);
    const mid = moment(start).add(end.diff(start) / 2, "ms");

    const term1Start = start.clone();
    const term1End = mid.clone();
    const term2Start = mid.clone().add(1, "day");
    const term2End = end.clone();

    const selectedClass = teacherPeriods.classes.find(
      (cls) => cls.class_id === Number(formData.selectedClass)
    );

    const filteredPeriods = teacherPeriods.periods.filter(
      (p) => p.class_name === selectedClass?.class_name
    );

    const term1Actual = countTermActualPeriods(
      filteredPeriods,
      term1Start,
      term1End
    );
    const term2Actual = countTermActualPeriods(
      filteredPeriods,
      term2Start,
      term2End
    );

    const term1Entered = tableData
      .filter((item) => item.term?.toLowerCase().includes("term1"))
      .reduce((acc, item) => acc + Number(item.numberofperiods || 0), 0);
    const term2Entered = tableData
      .filter((item) => item.term?.toLowerCase().includes("term2"))
      .reduce((acc, item) => acc + Number(item.numberofperiods || 0), 0);

    setTermStats({
      term1Start: term1Start.format("YYYY-MM-DD"),
      term1End: term1End.format("YYYY-MM-DD"),
      term2Start: term2Start.format("YYYY-MM-DD"),
      term2End: term2End.format("YYYY-MM-DD"),
      term1Days: term1End.diff(term1Start, "days") + 1,
      term2Days: term2End.diff(term2Start, "days") + 1,
      term1Actual,
      term2Actual,
      term1Entered,
      term2Entered,
    });
  }, [sessions, teacherPeriods, tableData]);

  const handleInputChange = (e, data) => {
    dispatch(
      handleTableData({
        name: e.target.name,
        value: e.target.value,
        id: data.id,
      })
    );
  };

  const handleBack = () => navigate("/uploadSyllabus", { state: formData });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const term1Exceeded = termStats.term1Entered > termStats.term1Actual;
    const term2Exceeded = termStats.term2Entered > termStats.term2Actual;

    if (term1Exceeded || term2Exceeded) {
      alert(
        "Submission blocked: One or more terms have exceeded the allowed number of periods."
      );
      return;
    }

    const selectedClass = teacherPeriods.classes.find(
      (cls) => cls.class_id === Number(formData.selectedClass)
    );
    const selectedSubject = teacherPeriods.subjects.find(
      (s) => s.subject_id === Number(formData.selectedSubject)
    );

    const payload = {
      teacherId: teacherPeriods.teacher_id,
      classId: selectedClass.class_id,
      className: selectedClass.class_name,
      subjectId: selectedSubject.subject_id,
      subjectName: selectedSubject.subject_name,
      periods: teacherPeriods.periods,
      syllabus: tableData,
    };

    try {
      const res = await dispatch(submitSyllabusThunk(payload)).unwrap();
      alert(res.message || "Submitted successfully");
      navigate("/viewSyllabus");
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const sortedTableData = tableData
    .slice()
    .sort((a, b) => Number(a.termId || 0) - Number(b.termId || 0));

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom>
        📄 Review Syllabus (Stage 2)
      </Typography>

      <Button variant="outlined" onClick={handleBack} sx={{ mb: 2 }}>
        ← Back
      </Button>

      {tableData.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Box mt={3} p={2} bgcolor="#f9f9fc" borderRadius={2}>
            <Typography variant="h6" gutterBottom>
              📊 Term-wise Preview
            </Typography>
            <Box sx={{ overflowX: "auto" }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "Term",
                      "Start",
                      "End",
                      "Days",
                      "Actual Periods",
                      "Entered Periods",
                      "Remarks",
                      "Actions",
                    ].map((head, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          backgroundColor: "#f0f0f0",
                          top: 0,
                          zIndex: 1,
                          position: "sticky",
                        }}
                      >
                        {head}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {["term1", "term2"].map((term) => {
                    const label = term === "term1" ? "Term 1" : "Term 2";
                    const actual = termStats[`${term}Actual`];
                    const entered = termStats[`${term}Entered`];
                    let remark = "";
                    if (entered < actual) {
                      remark = "You left some periods. Please add more.";
                    } else if (entered > actual) {
                      remark = "You have exceeded the expected periods.";
                    } else {
                      remark = "Perfect! Entered periods match the expected.";
                    }

                    return (
                      <TableRow key={term}>
                        <TableCell>{label}</TableCell>
                        <TableCell>{termStats[`${term}Start`]}</TableCell>
                        <TableCell>{termStats[`${term}End`]}</TableCell>
                        <TableCell>{termStats[`${term}Days`]}</TableCell>
                        <TableCell>{actual}</TableCell>
                        <TableCell>{entered}</TableCell>
                        <TableCell>
                          {entered > actual ? (
                            <Chip
                              label="Exceeded"
                              color="error"
                              variant="outlined"
                            />
                          ) : entered < actual ? (
                            <Chip
                              label="Incomplete"
                              color="warning"
                              variant="outlined"
                            />
                          ) : (
                            <Chip
                              label="Perfect"
                              color="success"
                              variant="outlined"
                            />
                          )}
                        </TableCell>

                        <TableCell>
                          {entered > actual ? (
                            <Chip label="✖" color="error" />
                          ) : entered < actual ? (
                            <Chip label="⚠" color="warning" />
                          ) : (
                            <Chip label="✔" color="success" />
                          )}
                          <Chip
                            label="👁️"
                            color="info"
                            clickable
                            onClick={() => {
                              const chapterList = tableData.filter(
                                (item) => item.term?.toLowerCase() === term
                              );

                              const entered = termStats[`${term}Entered`] || 0;
                              const actual = termStats[`${term}Actual`] || 0;
                              const remaining = actual - entered;

                              let chapterDetails = chapterList
                                .map(
                                  (item) =>
                                    `• ${item.chapter}: ${
                                      item.numberofperiods || 0
                                    }`
                                )
                                .join("\n");

                              const message = `
📘 ${term === "term1" ? "Term 1" : "Term 2"} Details

Chapters and Entered Periods:
${chapterDetails || "No chapters added."}

Total Entered: ${entered}
Remaining: ${remaining >= 0 ? remaining : 0}
    `;

                              alert(message);
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Box>
          <Paper elevation={2} sx={{ p: 1 }}>
            <form onSubmit={handleSubmit}>
              <Box
                sx={{
                  maxHeight: 400,
                  overflow: "auto",
                  border: "1px solid #ccc",
                  borderRadius: 1,
                }}
              >
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ backgroundColor: "#f0f0f0", p: "6px" }}>
                        S.No
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f0f0f0", p: "6px" }}>
                        Chapter Name
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f0f0f0", p: "6px" }}>
                        Term
                      </TableCell>
                      <TableCell sx={{ backgroundColor: "#f0f0f0", p: "6px" }}>
                        No of Periods
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedTableData.map((data, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ p: "6px" }}>{i + 1}</TableCell>
                        <TableCell sx={{ p: "6px" }}>{data.chapter}</TableCell>
                        <TableCell sx={{ p: "6px" }}>{data.term}</TableCell>
                        <TableCell sx={{ p: "6px" }}>
                          <TextField
                            name="numberofperiods"
                            type="number"
                            size="small"
                            required
                            fullWidth
                            value={data.numberofperiods || ""}
                            onChange={(e) => handleInputChange(e, data)}
                            sx={{
                              width: "60px",
                              "& input": {
                                p: "4px 8px",
                                fontSize: "0.875rem",
                              },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box textAlign="center" mt={2}>
                <Button variant="contained" color="primary" type="submit">
                  Submit
                </Button>
              </Box>
            </form>

            {/* Term-wise Summary Table */}
          </Paper>
        </Box>
      ) : (
        <Typography variant="body1" color="textSecondary">
          No Data Found
        </Typography>
      )}
    </Container>
  );
};

export default SyllabusStage2;
